const lineBreaks = {
  ">": " ",
  "&": "",
  "|": "\n",
  ".": null
};

const lineTypes = {
  "\\": "scene",
  ".": "action",
  _: "character",
  ":": "dialogue",
  "(": "parenthetical",
  "/": "transition",
  "=": "shot",
  "%": "note",
  "@": "actbreak"
};

export function parseTrelby(source, options = {}) {
  const filename = options.filename ?? null;
  const hadBom = source.startsWith("\uFEFF");
  const lines = source.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n").split("\n");
  const versionMatch = lines[0]?.match(/^#Version\s+(\d+)\s*$/);

  if (!versionMatch) {
    throw new Error("Not a Trelby screenplay: expected a #Version header.");
  }

  const version = Number(versionMatch[1]);

  if (version < 1 || version > 3) {
    throw new Error(`Unsupported Trelby file version ${version}.`);
  }

  const warnings = [];
  if (!hadBom) {
    warnings.push("The file does not include Trelby's UTF-8 byte-order mark; conversion continued.");
  }

  const titlePages = [];
  const headers = [];
  let currentTitlePage = null;
  let scriptStart = -1;
  let section = null;

  for (let index = 1; index < lines.length; index += 1) {
    const line = lines[index];
    const beginMatch = line.match(/^#Begin-(.+?)\s*$/);
    const endMatch = line.match(/^#End-(.+?)\s*$/);

    if (beginMatch) {
      section = beginMatch[1];
      continue;
    }

    if (endMatch) {
      section = null;
      continue;
    }

    if (section) {
      continue;
    }

    if (/^#Start-Script\s*$/.test(line)) {
      scriptStart = index + 1;
      break;
    }

    if (/^#Title-Page(?:\s|$)/.test(line)) {
      currentTitlePage = [];
      titlePages.push(currentTitlePage);
      continue;
    }

    if (line.startsWith("#Title-String ")) {
      if (!currentTitlePage) {
        currentTitlePage = [];
        titlePages.push(currentTitlePage);
      }

      currentTitlePage.push(parseTitleString(line.slice("#Title-String ".length), index + 1));
      continue;
    }

    if (line.startsWith("#Header-String ")) {
      headers.push(line.slice("#Header-String ".length));
      continue;
    }

    if (line.startsWith("#")) {
      continue;
    }

    if (looksLikeScriptLine(line)) {
      scriptStart = index;
      break;
    }
  }

  if (scriptStart < 0) {
    throw new Error("Trelby screenplay data was not found.");
  }

  const elements = parseElements(lines, scriptStart, warnings);

  return {
    type: "document",
    format: "trelby",
    version,
    filename,
    titlePages,
    headers,
    elements,
    warnings
  };
}

export function convertTrelbyToCue(source, options = {}) {
  const document = parseTrelby(source, options);
  const warnings = [...document.warnings];
  const metadata = extractMetadata(document.titlePages);
  const blocks = serializeElements(document.elements, warnings);
  const frontMatter = serializeFrontMatter(metadata);
  const cue = [frontMatter, blocks].filter(Boolean).join("\n\n").replace(/\n+$/, "") + "\n";

  return { cue, warnings, document };
}

function parseElements(lines, startIndex, warnings) {
  const elements = [];
  let current = null;

  function finishCurrent() {
    if (!current) {
      return;
    }

    elements.push(current);
    current = null;
  }

  for (let index = startIndex; index < lines.length; index += 1) {
    const rawLine = lines[index];

    if (rawLine === "" && index === lines.length - 1) {
      continue;
    }

    if (rawLine.length < 2) {
      warnings.push(`Line ${index + 1}: ignored a malformed Trelby screenplay line.`);
      continue;
    }

    const breakCode = rawLine[0];
    const typeCode = rawLine[1];
    const separator = Object.hasOwn(lineBreaks, breakCode) ? lineBreaks[breakCode] : " ";
    const type = lineTypes[typeCode] ?? "action";

    if (!Object.hasOwn(lineBreaks, breakCode)) {
      warnings.push(`Line ${index + 1}: unknown line-break code ${JSON.stringify(breakCode)}; treated as a space.`);
    }

    if (!Object.hasOwn(lineTypes, typeCode)) {
      warnings.push(`Line ${index + 1}: unknown element code ${JSON.stringify(typeCode)}; treated as action.`);
    }

    if (current && current.type !== type) {
      warnings.push(`Line ${index + 1}: element type changed before its final line; the elements were separated.`);
      finishCurrent();
    }

    if (!current) {
      current = {
        type,
        line: index + 1,
        text: ""
      };
    }

    current.text += rawLine.slice(2);

    if (separator === null) {
      finishCurrent();
    } else {
      current.text += separator;
    }
  }

  if (current) {
    warnings.push(`Line ${current.line}: the final element was not terminated; conversion continued.`);
    finishCurrent();
  }

  return elements;
}

function parseTitleString(value, line) {
  const fields = splitFields(value, 6);

  return {
    line,
    x: Number(fields[0]),
    y: Number(fields[1]),
    fontSize: Number(fields[2]),
    flags: fields[3],
    font: fields[4],
    text: unescapeTrelbyText(fields[6] ?? "")
  };
}

function splitFields(value, separatorCount) {
  const fields = [];
  let start = 0;

  for (let count = 0; count < separatorCount; count += 1) {
    const separator = value.indexOf(",", start);

    if (separator < 0) {
      fields.push(value.slice(start));
      return fields;
    }

    fields.push(value.slice(start, separator));
    start = separator + 1;
  }

  fields.push(value.slice(start));
  return fields;
}

function unescapeTrelbyText(value) {
  let result = "";

  for (let index = 0; index < value.length; index += 1) {
    if (value[index] !== "\\" || index + 1 >= value.length) {
      result += value[index];
      continue;
    }

    const next = value[index + 1];
    if (next === "n") {
      result += "\n";
      index += 1;
    } else if (next === "\\") {
      result += "\\";
      index += 1;
    } else {
      result += value[index];
    }
  }

  return result;
}

function extractMetadata(titlePages) {
  const strings = titlePages[0] ?? [];
  const metadata = {};
  const used = new Set();
  const centered = strings.filter((item) => item.flags.includes("c") && item.text.trim());
  const authorPattern = /^(?:(?:written|screenplay)\s+by|by)(?:\s*\n|\s+|$)/i;
  const authorItem = centered.find((item) => authorPattern.test(item.text.trim()));
  let author = null;

  if (authorItem) {
    author = authorItem.text.trim().replace(authorPattern, "").trim();
    if (author) {
      used.add(authorItem);
    }
  }

  const titleItem = centered
    .filter((item) => item !== authorItem)
    .sort((left, right) => right.fontSize - left.fontSize || left.y - right.y)[0];

  if (titleItem) {
    metadata.title = collapseMetadataText(titleItem.text);
    used.add(titleItem);
  }

  const subtitleItem = titleItem
    ? centered
      .filter((item) =>
        !used.has(item) &&
        item !== authorItem &&
        item.y > titleItem.y &&
        (!authorItem || item.y < authorItem.y) &&
        !/\bdraft\b/i.test(item.text)
      )
      .sort((left, right) => left.y - right.y)[0]
    : null;
  if (subtitleItem) {
    metadata.subtitle = collapseMetadataText(subtitleItem.text);
    used.add(subtitleItem);
  }

  if (author) {
    metadata.author = collapseMetadataText(author, " & ");
  }

  const draftItem = strings.find((item) => !used.has(item) && /\bdraft\b/i.test(item.text));
  if (draftItem) {
    metadata.draft = collapseMetadataText(draftItem.text);
    used.add(draftItem);
  }

  const remaining = strings.filter((item) => !used.has(item) && item.text.trim());
  if (remaining.length === 1) {
    metadata.contact = collapseMetadataText(remaining[0].text, " / ");
  } else if (remaining.length > 1) {
    metadata["title-page-text"] = remaining
      .map((item) => collapseMetadataText(item.text, " / "))
      .join(" | ");
  }

  return metadata;
}

function collapseMetadataText(value, separator = " ") {
  return value
    .split("\n")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(separator);
}

function serializeFrontMatter(metadata) {
  const entries = Object.entries(metadata);
  if (entries.length === 0) {
    return "";
  }

  return ["---", ...entries.map(([key, value]) => `${key}: ${value}`), "---"].join("\n");
}

function serializeElements(elements, warnings) {
  const blocks = [];
  let lastCharacter = null;

  for (let index = 0; index < elements.length; index += 1) {
    const element = elements[index];

    if (element.type === "character") {
      lastCharacter = flattenText(element.text);
      const dialogue = serializeDialogue(elements, index + 1, lastCharacter);
      blocks.push(dialogue.block);
      index = dialogue.nextIndex - 1;
      continue;
    }

    if (["dialogue", "parenthetical"].includes(element.type) && lastCharacter) {
      const dialogue = serializeDialogue(elements, index, lastCharacter);
      blocks.push(dialogue.block);
      index = dialogue.nextIndex - 1;
      continue;
    }

    switch (element.type) {
      case "scene":
        blocks.push(`.${flattenText(element.text)}`);
        lastCharacter = null;
        break;
      case "action":
        blocks.push(formatHardBreaks(element.text, escapeActionLine).join("\n"));
        break;
      case "shot":
        blocks.push(`! ${flattenText(element.text)}`);
        break;
      case "transition":
        blocks.push(`> ${flattenText(element.text)}`);
        break;
      case "note":
        blocks.push(`[[${flattenText(element.text).replaceAll("]]", "] ]")}]]`);
        break;
      case "actbreak":
        blocks.push(`# ${flattenText(element.text)}`);
        lastCharacter = null;
        break;
      case "dialogue":
      case "parenthetical":
        warnings.push(`Line ${element.line}: ${element.type} had no preceding character cue; preserved as action.`);
        blocks.push(formatHardBreaks(element.text, escapeActionLine).join("\n"));
        break;
      default:
        blocks.push(formatHardBreaks(element.text, escapeActionLine).join("\n"));
    }
  }

  return blocks.join("\n\n");
}

function serializeDialogue(elements, startIndex, character) {
  const lines = [`@${character}`];
  let next = startIndex;

  while (next < elements.length && ["dialogue", "parenthetical"].includes(elements[next].type)) {
    const element = elements[next];
    if (element.type === "parenthetical") {
      lines.push(formatParenthetical(element.text));
    } else {
      lines.push(...formatHardBreaks(element.text));
    }
    next += 1;
  }

  return { block: lines.join("\n"), nextIndex: next };
}

function formatParenthetical(value) {
  const text = flattenText(value);
  return text.startsWith("(") && text.endsWith(")") ? text : `(${text})`;
}

function flattenText(value) {
  return value.replace(/\n+/g, " ").trim();
}

function formatHardBreaks(value, transform = (line) => line) {
  const lines = value.split("\n");
  return lines.map((line, index) => {
    const transformed = transform(line.trim());
    return index < lines.length - 1 ? `${transformed} \\\\` : transformed;
  });
}

function escapeActionLine(line) {
  if (line.startsWith("\\") || looksLikeCueBlock(line)) {
    return `\\${line}`;
  }

  return line;
}

function looksLikeCueBlock(line) {
  return /^(?:#{1,6}\s+|\.|@|!|>|\/\/|\[\[)/.test(line);
}

function looksLikeScriptLine(line) {
  return line.length >= 2 && Object.hasOwn(lineBreaks, line[0]) && Object.hasOwn(lineTypes, line[1]);
}
